<template>
  <div class="main-map">
    <l-map
      ref="map"
      :zoom="zoom.level"
      :center="center"
      :options="mapOptions"
      :maxBounds="maxBounds"
      :minZoom="2"
      @update:bounds="setMapBounds"
      style="z-index: 0; height: 100%"
    >
      <l-choropleth-layer
        :data="projectBudgetsPerCountry"
        titleKey="countryIso3Code"
        idKey="countryIso3Code"
        :value="value"
        geojsonIdKey="iso3code"
        :geojson="$options.africaGeojson"
        :colorScale="colorScale"
        :strokeWidth="0"
        :currentStrokeWidth="2"
        currentStrokeColor="FFFFFF"
      >
        <template slot-scope="props">
          <l-info-control
            :item="props.currentItem"
            :unit="humanizeBudget(props.currentItem)"
            :secondaryUnit="humanizeProjectsCount(props.currentItem)"
            :areaTitle="getCountryName(props.currentItem)"
            :colorScale="colorScale"
            :valueBounds="budgetBounds"
            position="topright"
          />
        </template>
      </l-choropleth-layer>
      <l-tile-layer :url="url"/>
      <l-marker-cluster v-if="companies.length" :options="clusterOptions" ref="cluster">
        <marker-popup
          v-for="company in filteredCompanies"
          :position="latLng(company.coordinates.lat, company.coordinates.lng)"
          :company="company"
          :key="company._id"
        />
      </l-marker-cluster>
    </l-map>
    <transition name="slide" mode="out-in">
      <company-card v-if="card.kind === 'company'" :company="card"/>
    </transition>
    <transition name="slide" mode="out-in">
      <project-card v-if="card.kind === 'project'" :project="card"/>
    </transition>
    <div class="map-disclaimer">
      <p>
        This data represents a snapshot of companies taken September and October 2019. The designations employed and the
        presentation of material on this map do not imply the expression of any opinion whatsoever on the part of the
        Secretariat of the United Nations or UNDP concerning the legal status of any country, territory, city or area or
        its authorities, or concerning the delimitation of its frontiers or boundaries.
      </p>
    </div>
  </div>
</template>

<script>
import { latLng } from 'leaflet'
import { mapGetters, mapActions } from 'vuex'
import { LMap, LTileLayer } from 'vue2-leaflet'
import Vue2LeafletMarkerCluster from 'vue2-leaflet-markercluster'
import { ChoroplethLayer } from 'vue-choropleth'
import numbro from 'numbro'
import InfoControl from '@/components/main-map-info-control'
import MarkerPopup from '@/components/marker-popup'
import CompanyCard from '@/components/company-card'
import ProjectCard from '@/components/projects/project-card'
import dataService from '@/services/data.service'
import optionsData from '@/data/options'

const mapboxToken = process.env.VUE_APP_MAPBOX_KEY

const currencyFormat = {
  average: true,
  mantissa: 2,
  optionalMantissa: true,
  currencyPosition: 'prefix',
}

const colorScale = ['e7d090', 'e9ae7b', 'de7062']

export default {
  name: 'main-map',
  components: {
    LMap,
    LTileLayer,
    MarkerPopup,
    LMarkerCluster: Vue2LeafletMarkerCluster,
    CompanyCard,
    ProjectCard,
    'l-choropleth-layer': ChoroplethLayer,
    'l-info-control': InfoControl,
  },
  africaGeojson: dataService.getAfricaGeojson(),
  data() {
    return {
      url:
        'https://api.tiles.mapbox.com/v4/mapbox.light/{z}/{x}/{y}.png?access_token=' +
        mapboxToken,
      mapOptions: {
        zoomSnap: 0.5,
      },
      clusterOptions: {
        showCoverageOnHover: false,
      },
      colorScale,
      value: {
        key: 'totalBudget',
        metric: 'total budget',
      },
    }
  },
  computed: {
    ...mapGetters({
      companies: 'companies/companies',
      inBoundsProjects: 'projects/inBoundsProjects',
      center: 'map/center',
      zoom: 'map/zoom',
      card: 'map/card',
      resetZoom: 'map/reset',
    }),
    filteredCompanies() {
      return this.companies.filter(
        company => company.coordinates.lat && company.coordinates.lng
      )
    },
    maxBounds() {
      const africanBounds = [[40, -25], [-37, 60]]
      return africanBounds
    },
    projectBudgetsPerCountry() {
      const budgetsPerCountry = this.inBoundsProjects.reduce((acc, project) => {
        acc[project.baseCountryIsoCode] = acc[project.baseCountryIsoCode] || {
          projectsCount: 0,
          projectsBudget: 0,
        }
        acc[project.baseCountryIsoCode].projectsCount += +1
        acc[project.baseCountryIsoCode].projectsBudget += +project.total_budget
        return acc
      }, {})

      Object.keys(optionsData.countriesList).forEach(code => {
        if (!budgetsPerCountry[code]) {
          budgetsPerCountry[code] = 'No projects' // a workaround to put empty countries on the map
        }
      })

      return Object.keys(budgetsPerCountry).map(code => ({
        countryIso3Code: code,
        countryName: optionsData.countriesList[code].name,
        totalBudget: budgetsPerCountry[code].projectsBudget,
        totalProjectsCount: budgetsPerCountry[code].projectsCount,
      }))
    },
    budgetBounds() {
      let min = 0
      let max = 0
      this.projectBudgetsPerCountry.forEach(item => {
        if (item.totalBudget < min) {
          min = item.totalBudget
        }
        if (item.totalBudget > max) {
          max = item.totalBudget
        }
      })
      return { min, max }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.setDefaultZoomLevel()
    })
  },
  watch: {
    resetZoom() {
      this.$nextTick(() => {
        this.setDefaultZoomLevel()
      })
    },
  },
  methods: {
    ...mapActions({
      setFilter: 'companies/setFilter',
      setBounds: 'map/setBounds',
    }),
    latLng,
    setDefaultZoomLevel() {
      const bounds = this.maxBounds
      this.$refs.map.mapObject.fitBounds(bounds)
    },
    setMapBounds(bounds) {
      this.setBounds(bounds)
    },
    getCountryName(item) {
      if (item && item.name && optionsData.countriesList[item.name]) {
        return optionsData.countriesList[item.name].name
      }
      return ''
    },
    humanizeProjectsCount(item) {
      if (item && item.name) {
        const countryData = this.projectBudgetsPerCountry.find(
          countryObj => countryObj.countryIso3Code === item.name
        )
        if (countryData && countryData.totalProjectsCount) {
          return `${countryData.totalProjectsCount} project${
            countryData.totalProjectsCount > 1 ? 's' : ''
          } total`
        }
      }
      return ''
    },
    humanizeBudget(item) {
      if (item) {
        if (item.value === undefined || item.value === 'No projects') {
          return `No development projects with current filter`
        }
        return `$${numbro(item.value).format(
          currencyFormat
        )} development projects budget`
      }
      return ''
    },
  },
}
</script>

<style lang="scss" scoped>
$cluster-color: rgba(181, 226, 140, 0.6);
$cluster-color-div: rgba(110, 204, 57, 0.6);
@import '~leaflet.markercluster/dist/MarkerCluster.css';
@import '~leaflet.markercluster/dist/MarkerCluster.Default.css';
.main-map {
  height: 100%;
  position: relative;
  & ::v-deep .marker-cluster-small,
  & ::v-deep .marker-cluster-medium,
  & ::v-deep .marker-cluster-large {
    background-color: $cluster-color;
  }
  & ::v-deep .marker-cluster-small div,
  & ::v-deep .marker-cluster-medium div,
  & ::v-deep .marker-cluster-large div {
    background-color: $cluster-color-div;
  }
}
.map-disclaimer {
  position: absolute;
  background: transparent;
  pointer-events: none;
  bottom: 0;
  padding: 12px;
  font-size: 12px;
  line-height: 1.2;
  p {
    margin-bottom: 10px;
    color: rgba(0, 0, 0, 0.7);
  }
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.1s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-enter,
.slide-leave-to {
  transform: translateX(100px);
  opacity: 0;
}
</style>
